import { Col, Row, Space, Tag } from "antd"
import { useEffect } from "react"
import { Seller } from "../../../../domain/entities/Seller"
import { useTranslation } from "react-i18next"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { FormField } from "../../../components/Form/FormField"
import config from "../../../../config/config"
import { DinamicSelectorWeightForm } from "../../sellersServices/components/DinamicSelectorWeightForm"
import { Microsite } from "../../../../domain/entities/Microsite"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { MicrositesViewModel } from "../../microSites/MicrositesViewModel"
import { observer } from "mobx-react"
import Switch from "../../../components/Switch/Switch"
import { SellerServicesViewModel } from "../../sellersServices/SellerServicesViewModel"
import { SellerService } from "../../../../domain/entities/SellerService"

interface Props {
	seller: Partial<Seller>
	inputDisabled?: boolean
	onChangeText?: (value: Partial<Seller>) => void
}

const AdministrativeInformationFormSection = ({ seller, inputDisabled, onChangeText }: Props) => {
	const { t } = useTranslation("sellers", { keyPrefix: "form.fields" })
	const { t: tCountries } = useTranslation("countries")
	const micrositesViewModel = useContainerInjection<MicrositesViewModel>("MicrositesViewModel")
	const sellerServicesViewModel = useContainerInjection<SellerServicesViewModel>("SellerServicesViewModel")

	useEffect(() => {
		const sellerId = Number(seller.id)
		if (!Number.isFinite(sellerId)) return
		sellerServicesViewModel.fetchSellersServicesBySellerId(sellerId)
	}, [seller.id, sellerServicesViewModel])
	return (
		<>
			<Row gutter={[24, 0]}>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						disabled={inputDisabled}
						key={"fiscalName"}
						label={t("fiscalName")}
						name={"fiscalName"}
						onChange={(fiscalName: string) => onChangeText && onChangeText({ fiscalName })}
					/>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Select
						disabled={inputDisabled}
						mandatory
						key={"fiscalCountry"}
						label={t("fiscalCountry")}
						name={"fiscalCountry"}
						onChange={(fiscalCountry: string) => onChangeText && onChangeText({ fiscalCountry })}
						options={config.tax.fiscalCountries.map(country => ({
							value: country,
							label: tCountries(country),
							key: country
						}))}
					/>
				</Col>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						disabled={inputDisabled}
						key={"fiscalAddress"}
						label={t("fiscalAddress")}
						name={"fiscalAddress"}
						onChange={(fiscalAddress: string) => onChangeText && onChangeText({ fiscalAddress })}
					/>
				</Col>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						disabled={inputDisabled}
						inputType={InputType.NUMBER}
						key={"paymentDays"}
						label={t("paymentDays")}
						name={"paymentDays"}
						mandatory
						onChange={(paymentDays: string) => onChangeText && onChangeText({ paymentDays })}
						style={{ marginBottom: "9px" }}
						customFieldPosition={"relative"}
					/>
				</Col>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						disabled={inputDisabled}
						inputType={InputType.NUMBER}
						key={"feePercentage"}
						label={t("feePercentage")}
						name={"feePercentage"}
						mandatory
						preFix={"%"}
						onChange={(feePercentage: string) => onChangeText && onChangeText({ feePercentage })}
						style={{ marginBottom: "9px" }}
						customFieldPosition={"relative"}
					/>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						disabled={inputDisabled}
						key={"nif"}
						label={t("nif")}
						name={"nif"}
						mandatory
						onChange={(nif: string) => onChangeText && onChangeText({ nif })}
					/>
				</Col>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						disabled={inputDisabled}
						key={"iban"}
						label={t("iban")}
						name={"iban"}
						mandatory
						onChange={(iban: string) => onChangeText && onChangeText({ iban })}
					/>
				</Col>
			</Row>
			<Row gutter={[24, 0]}>
				<Col xl={24} md={24} xs={24} style={{ display: "flex", flexDirection: "column" }}>
					<DinamicSelectorWeightForm<Microsite>
						label={t("feePercentage")}
						weightLabel={t("feePercentage") + " (%)"}
						name="micrositeAssociations"
						onAdd={microsite =>
							onChangeText &&
							onChangeText({
								micrositeAssociations: (seller.micrositeAssociations ?? []).concat([
									["ES", "MX"].includes(microsite.id.toString())
										? {
												sellerId: Number(seller.id),
												marketplace: microsite.id.toString(),
												feePercentage: Number(microsite.weight)
										  }
										: {
												sellerId: Number(seller.id),
												micrositeId: Number(microsite.id),
												feePercentage: Number(microsite.weight)
										  }
								])
							})
						}
						data={[
							{
								id: "ES" as string | number,
								label: `${t("marketplace")} ES`,
								value: "ES",
								keepLabel: true
							},
							{
								id: "MX" as string | number,
								label: `${t("marketplace")} MX`,
								value: "MX",
								keepLabel: true
							}
						]
							.concat(
								micrositesViewModel.microsites.map(m => ({
									id: m.id!,
									label: m.slug!,
									value: m.id!,
									keepLabel: false
								}))
							)
							.filter(
								m =>
									!seller.micrositeAssociations?.some(
										a =>
											a.micrositeId?.toString() === m.id?.toString() ||
											a.marketplace?.toString() === m.id?.toString()
									)
							)}
						loading={micrositesViewModel.isLoading}
						disabled={micrositesViewModel.isLoading || inputDisabled}
					/>
					<Space size={[0, 8]} wrap className="mb-5">
						{seller.micrositeAssociations?.map((association, idx) => {
							const microsite = micrositesViewModel.microsites.find(
								microsite => microsite.id?.toString() === association.micrositeId?.toString()
							)
							let label = `${microsite?.id} - ${microsite?.slug}`
							if (!microsite) {
								label = `${t("marketplace")} ${association.marketplace}`
							}
							return microsite || association.marketplace ? (
								<Tag
									key={microsite?.id ?? association.marketplace}
									closable
									style={{
										userSelect: "none",
										height: 30,
										display: "flex",
										alignItems: "center"
									}}
									onClose={() =>
										seller.micrositeAssociations &&
										onChangeText &&
										onChangeText({
											micrositeAssociations: seller.micrositeAssociations.filter(
												(_, index) => index !== idx
											)
										})
									}
								>
									{`${label} (${association.feePercentage}%)`}
								</Tag>
							) : null
						})}
					</Space>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col xl={24} md={24} xs={24} style={{ display: "flex", flexDirection: "column" }}>
					<DinamicSelectorWeightForm<SellerService>
						label={t("feePercentageService")}
						weightLabel={t("feePercentageService") + " (%)"}
						name="serviceAssociations"
						onAdd={service =>
							onChangeText &&
							onChangeText({
								serviceAssociations: [
									...(seller.serviceAssociations ?? []),
									{
										sellerId: Number(seller.id),
										serviceId: Number(service.id),
										feePercentage: Number(service.weight)
									}
								]
							})
						}
						data={[
							...sellerServicesViewModel.sellerServices
								.map(s => ({
									id: s.id!,
									label: s.title!,
									keepLabel: false
								}))
								.filter(
									m =>
										!seller.serviceAssociations?.some(
											a => a.serviceId?.toString() === m.id?.toString()
										)
								)
						]}
						loading={micrositesViewModel.isLoading}
						disabled={micrositesViewModel.isLoading || inputDisabled}
					/>
					<Space size={[0, 8]} wrap className="mb-5">
						{seller.serviceAssociations?.map((association, idx) => {
							const service = sellerServicesViewModel.sellerServices?.find(
								service => service.id?.toString() === association.serviceId?.toString()
							)
							let label = `${service?.id} - ${service?.title}`
							return service ? (
								<Tag
									key={service?.id}
									closable
									style={{
										userSelect: "none",
										height: 30,
										display: "flex",
										alignItems: "center"
									}}
									onClose={() =>
										seller.serviceAssociations &&
										onChangeText &&
										onChangeText({
											serviceAssociations: seller.serviceAssociations.filter(
												(_, index) => index !== idx
											)
										})
									}
								>
									{`${label} (${association.feePercentage}%)`}
								</Tag>
							) : null
						})}
					</Space>
				</Col>
			</Row>
			<Row gutter={[24, 0]} className="pl-1">
				<Col span={4} xl={4} md={4} xs={24}>
					<Switch
						key={"feeException"}
						title={t("feeException")}
						checked={seller.feeException}
						onChange={feeException => onChangeText && onChangeText({ feeException })}
						disabled={inputDisabled}
					/>
				</Col>
			</Row>
		</>
	)
}

export default observer(AdministrativeInformationFormSection)
